import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../lib/auth-context";
import { toast } from "sonner";

export default function RegisterPage() {
    const nav = useNavigate();
    const { register } = useAuth();
    const [params] = useSearchParams();
    const initialRole = params.get("role") || "consumer";

    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        password: "",
        role: initialRole === "admin" ? "consumer" : initialRole,
        business_name: "",
        country: "NG",
        currency: "NGN",
    });
    const [busy, setBusy] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await register(form);
            toast.success(`Welcome, ${u.full_name.split(" ")[0]}!`);
            const home = u.role === "business" ? "/business" : "/consumer";
            nav(home, { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-6">
                    <Logo size={48} asLink />
                </div>
                <div className="bg-white rounded-3xl p-8 border border-slate-200 product-shadow" data-testid="register-card">
                    <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Create your account</h1>
                    <p className="text-sm text-slate-500 mt-1">Join OnePassCredit in under a minute</p>

                    <div className="grid grid-cols-2 gap-2 mt-5">
                        <button
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, role: "consumer" }))}
                            data-testid="role-consumer-btn"
                            className={`p-3 rounded-xl border-2 text-sm font-semibold transition ${
                                form.role === "consumer"
                                    ? "border-[#7000FF] bg-purple-50 text-[#7000FF]"
                                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}
                        >
                            Individual
                        </button>
                        <button
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, role: "business" }))}
                            data-testid="role-business-btn"
                            className={`p-3 rounded-xl border-2 text-sm font-semibold transition ${
                                form.role === "business"
                                    ? "border-[#0066FF] bg-blue-50 text-[#0066FF]"
                                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}
                        >
                            Business
                        </button>
                    </div>

                    <form onSubmit={onSubmit} className="mt-5 space-y-3.5">
                        <div>
                            <Label className="text-sm">Full name</Label>
                            <Input
                                value={form.full_name}
                                onChange={onChange("full_name")}
                                required
                                placeholder="Jane Doe"
                                className="mt-1.5 h-11 rounded-xl"
                                data-testid="register-name-input"
                            />
                        </div>
                        {form.role === "business" && (
                            <div>
                                <Label className="text-sm">Business name</Label>
                                <Input
                                    value={form.business_name}
                                    onChange={onChange("business_name")}
                                    required
                                    placeholder="Acme Ltd."
                                    className="mt-1.5 h-11 rounded-xl"
                                    data-testid="register-business-input"
                                />
                            </div>
                        )}
                        <div>
                            <Label className="text-sm">Email</Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={onChange("email")}
                                required
                                placeholder="you@email.com"
                                className="mt-1.5 h-11 rounded-xl"
                                data-testid="register-email-input"
                            />
                        </div>
                        <div>
                            <Label className="text-sm">Phone</Label>
                            <Input
                                value={form.phone}
                                onChange={onChange("phone")}
                                required
                                placeholder="+2348012345678"
                                className="mt-1.5 h-11 rounded-xl"
                                data-testid="register-phone-input"
                            />
                        </div>
                        <div>
                            <Label className="text-sm">Password</Label>
                            <Input
                                type="password"
                                value={form.password}
                                onChange={onChange("password")}
                                required
                                minLength={6}
                                placeholder="At least 6 characters"
                                className="mt-1.5 h-11 rounded-xl"
                                data-testid="register-password-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-sm">Country</Label>
                                <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="register-country-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="NG">🇳🇬 Nigeria</SelectItem>
                                        <SelectItem value="KE">🇰🇪 Kenya</SelectItem>
                                        <SelectItem value="UG">🇺🇬 Uganda</SelectItem>
                                        <SelectItem value="TZ">🇹🇿 Tanzania</SelectItem>
                                        <SelectItem value="GH">🇬🇭 Ghana</SelectItem>
                                        <SelectItem value="ZA">🇿🇦 South Africa</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-sm">Currency</Label>
                                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="register-currency-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="NGN">NGN ₦ (Nigeria)</SelectItem>
                                        <SelectItem value="KES">KES KSh (Kenya)</SelectItem>
                                        <SelectItem value="UGX">UGX USh (Uganda)</SelectItem>
                                        <SelectItem value="TZS">TZS TSh (Tanzania)</SelectItem>
                                        <SelectItem value="GHS">GHS GH₵ (Ghana)</SelectItem>
                                        <SelectItem value="USD">USD $</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            disabled={busy}
                            className="w-full h-11 rounded-xl bg-[#0A1930] hover:bg-[#0d1f3d] font-semibold mt-2"
                            data-testid="register-submit-btn"
                        >
                            {busy ? "Creating..." : "Create account"}
                        </Button>
                    </form>

                    <p className="text-center text-sm text-slate-500 mt-5">
                        Already have an account?{" "}
                        <Link to="/login" className="text-[#0066FF] font-semibold" data-testid="register-go-login">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
